import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Bell, LogOut, Wallet, Target, Coins, TrendingUp, Package } from 'lucide-react';
import WarningBanner from '@/components/salesman/WarningBanner';
import BottomNav from '@/components/salesman/BottomNav';
import api, { fmtIDR, currentMonth } from '@/lib/api';
import { Link } from 'react-router-dom';

export default function SalesmanDashboard() {
  const { user, logout } = useAuth();
  const [perf, setPerf] = useState(null);
  const [txs, setTxs] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        api.get(`/kpi/my-performance?month=${currentMonth()}`),
        api.get('/transactions?limit=5'),
      ]);
      setPerf(p.data);
      setTxs(t.data);
    } catch (e) {
      console.error('[SalesmanDashboard] gagal load performance & transaksi:', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const progressPct = Math.min(100, perf?.progress_pct || 0);

  return (
    <div className="min-h-screen bg-background pb-24">
      <WarningBanner />

      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-teal-700 text-primary-foreground px-4 pt-6 pb-8 rounded-b-3xl relative overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs opacity-90">Selamat datang,</div>
            <div className="font-heading font-semibold text-lg truncate">{user?.full_name}</div>
            <div className="text-xs opacity-90">@{user?.username} • {currentMonth()}</div>
          </div>
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-white/10" onClick={logout} data-testid="salesman-logout-button">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="px-4 -mt-6 space-y-4 max-w-md mx-auto">
        {/* Main KPI */}
        <Card className="shadow-md">
          <CardContent className="p-5 space-y-4">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Omset Bulan Ini</div>
              <div className="text-3xl font-heading font-semibold tabular-nums" data-testid="salesman-revenue">{fmtIDR(perf?.revenue || 0)}</div>
              <div className="text-xs text-muted-foreground mt-1">{perf?.transactions || 0} transaksi</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-muted-foreground">Progress ke Target</div>
                <div className="text-xs font-medium tabular-nums" data-testid="salesman-progress-pct">{progressPct.toFixed(1)}%</div>
              </div>
              <Progress value={progressPct} className="h-3" />
              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                <span>{fmtIDR(perf?.revenue || 0)}</span>
                <span>Target {perf?.target > 0 ? fmtIDR(perf.target) : 'belum diset'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commission wallet + breakdown */}
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-md border-0">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center"><Coins className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="text-xs opacity-90">Estimasi Komisi Bulan Ini</div>
                <div className="text-2xl font-heading font-semibold tabular-nums" data-testid="salesman-commission">{fmtIDR(perf?.commission || 0)}</div>
              </div>
            </div>
            {/* Breakdown non-besi vs besi */}
            {perf?.commission_breakdown && (
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/20">
                <div className="rounded-lg bg-white/10 p-2" data-testid="salesman-commission-non-iron">
                  <div className="text-[10px] opacity-90 uppercase tracking-wide">Non-Besi ({((perf?.commission_config?.non_iron_rate || 0) * 100).toFixed(1)}%)</div>
                  <div className="text-base font-semibold tabular-nums">{fmtIDR(perf.commission_breakdown.non_iron || 0)}</div>
                </div>
                <div className="rounded-lg bg-white/10 p-2" data-testid="salesman-commission-iron">
                  <div className="text-[10px] opacity-90 uppercase tracking-wide">Besi & Baja</div>
                  <div className="text-base font-semibold tabular-nums">{fmtIDR(perf.commission_breakdown.iron || 0)}</div>
                  <div className="text-[9px] opacity-80 tabular-nums">{(perf.commission_breakdown.iron_total_kg || 0).toFixed(2)} kg dijual</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mini cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card><CardContent className="p-4">
            <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2"><Target className="h-4 w-4" /></div>
            <div className="text-xs text-muted-foreground">Target</div>
            <div className="font-heading font-semibold tabular-nums text-sm">{perf?.target > 0 ? fmtIDR(perf.target) : '—'}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="h-9 w-9 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center mb-2"><TrendingUp className="h-4 w-4" /></div>
            <div className="text-xs text-muted-foreground">Sisa Target</div>
            <div className="font-heading font-semibold tabular-nums text-sm">{fmtIDR(Math.max(0, (perf?.target || 0) - (perf?.revenue || 0)))}</div>
          </CardContent></Card>
        </div>

        {/* Top products */}
        {perf?.top_products?.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" />Barang Terlaris Saya</CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-2">
              {perf.top_products.map((p, i) => (
                <div key={p.product_id} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="secondary">{i + 1}</Badge>
                    <span className="truncate">{p.name}</span>
                  </div>
                  <div className="tabular-nums text-muted-foreground">{p.quantity} pcs</div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent transactions */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Transaksi Terbaru Saya</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {txs.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">Belum ada transaksi</div>
            ) : (
              <ul className="space-y-2">
                {txs.map((t) => (
                  <li key={t.id} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                    <div className="min-w-0">
                      <div className="font-mono text-xs truncate">{t.invoice_no}</div>
                      <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString('id-ID')}</div>
                    </div>
                    <div className="tabular-nums font-semibold">{fmtIDR(t.total)}</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="text-xs text-muted-foreground text-center pt-2">
          Komisi dihitung otomatis 2% dari total omset. Data diperbarui real-time.
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
